// Shared aggregation + types for the map-key chart bodies.
//
// All four count/sum bodies (pie + bar × count + sum) fetch the same
// /api/features/values/{id} payload and bucket rows by `key_label`.
// They differ only in the aggregation (count vs sum) and the
// visualization (pie vs bar) — the rest is identical, so factored out.

import type { DateWindow } from '../windowRange';
import { withinWindow } from '../windowRange';
import { OTHER_COLOR, assignSliceColors } from './chartColors';

export interface MapValueRow {
  date: string; // ISO timestamp
  decimal_value: number | null;
  freeform_value: string | null;
  reference_value: number | null;
  latitude: number | null;
  longitude: number | null;
  key_freeform_value: string | null;
  key_decimal_value: number | null;
  key_reference_value: number | null;
  key_label: string | null;
}

export interface MapBucket {
  name: string;
  value: number;
  color: string;
}

export type AggregationMode = 'count' | 'sum';

// Aggregate rows into buckets keyed by `key_label`. Rows without a label
// (non-map rows, or map rows missing a key) are skipped. For 'sum' mode,
// rows without a numeric `decimal_value` are skipped — that's
// intentional, since sum aggregation only makes sense for numeric map
// values, and a non-numeric map shouldn't surface the sum charts in its
// cycle anyway.
//
// Result is sorted desc by aggregate. When the bucket count exceeds
// `topN` (default 9), the long tail folds into a single "Other" bucket
// so pie/bar visuals stay legible. Set `topN = Infinity` to disable.
export function aggregateMapRows(
  rows: MapValueRow[],
  mode: AggregationMode,
  win: DateWindow,
  topN: number = 9,
): MapBucket[] {
  const totals = new Map<string, number>();
  for (const r of rows) {
    const label = r.key_label;
    if (!label) continue;
    if (!withinWindow(new Date(r.date).getTime(), win)) continue;
    if (mode === 'sum') {
      if (r.decimal_value == null) continue;
      totals.set(label, (totals.get(label) ?? 0) + r.decimal_value);
    } else {
      totals.set(label, (totals.get(label) ?? 0) + 1);
    }
  }

  const ordered = [...totals.entries()].sort((a, b) => b[1] - a[1]);
  if (ordered.length <= topN) {
    const colors = assignSliceColors(ordered.map(([name]) => name));
    return ordered.map(([name, value], i) => ({ name, value, color: colors[i] }));
  }

  const head = ordered.slice(0, topN);
  const tail = ordered.slice(topN);
  const otherTotal = tail.reduce((acc, [, v]) => acc + v, 0);
  const headColors = assignSliceColors(head.map(([name]) => name));
  const buckets: MapBucket[] = head.map(([name, value], i) => ({
    name,
    value,
    color: headColors[i],
  }));
  buckets.push({ name: 'Other', value: otherTotal, color: OTHER_COLOR });
  return buckets;
}

export interface MapKeySeriesPoint {
  key: number;        // ordered numeric key (key_decimal_value)
  avg: number;        // mean of decimal_value for rows sharing this key
  count: number;      // pair count contributing to avg
}

// Aggregate rows for Q1 (orderable key × aggregable value). Buckets on
// the row's `key_decimal_value` and averages `decimal_value` within each
// bucket. Sorted by key asc so the line reads left-to-right as
// "key increasing." Rows missing either column are skipped (consistent
// with `aggregateMapRows`'s skip-when-incoherent rule). Window filter
// matches `aggregateMapRows`.
export function aggregateMapKeySeries(
  rows: MapValueRow[],
  win: DateWindow,
): MapKeySeriesPoint[] {
  const sums = new Map<number, { total: number; count: number }>();
  for (const r of rows) {
    if (r.key_decimal_value == null || r.decimal_value == null) continue;
    if (!withinWindow(new Date(r.date).getTime(), win)) continue;
    const k = r.key_decimal_value;
    const acc = sums.get(k);
    if (acc) {
      acc.total += r.decimal_value;
      acc.count += 1;
    } else {
      sums.set(k, { total: r.decimal_value, count: 1 });
    }
  }
  return [...sums.entries()]
    .map(([key, { total, count }]) => ({ key, avg: total / count, count }))
    .sort((a, b) => a.key - b.key);
}
